import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import { ArrowRight } from 'lucide-react';

const COPY = {
  fr: {
    eyebrow: 'La plateforme',
    title: 'FretManager, de la collecte à la livraison',
    lede: "Trois moments structurent un envoi. La plateforme les traite de bout en bout, "
      + 'sans ressaisie et sans perte de traçabilité entre les équipes.',
    more: 'En savoir plus',
    blocks: [
      {
        id: 'registration',
        title: 'Enregistrement des colis',
        image: '/landing/registration.jpg',
        alt: "Colis étiquetés et imprimante d'étiquettes d'expédition",
        text: "Le colis est enregistré en quatre étapes : trajet, expéditeur et destinataire, "
          + 'contenu, paiement. Le prix est proposé à partir de la grille tarifaire de la route, '
          + 'la facture est émise dans la foulée et le client reçoit sa confirmation.',
        points: [
          'Plusieurs colis pour un même client en une seule saisie',
          'Déclaration par le client via QR code, validée par le partenaire',
          'Code de suivi unique et étiquette imprimable par colis',
        ],
      },
      {
        id: 'departures',
        title: 'Départs et chargement',
        image: '/landing/departures.jpg',
        alt: 'Avion cargo chargé de palettes de fret sur le tarmac',
        text: 'Chaque départ regroupe ses colis avec sa date limite de dépôt, son vol ou sa '
          + 'traversée. Le chargement se fait au scan : les colis attendus mais absents sont '
          + 'signalés avant le départ, pas après.',
        points: [
          'Aérien et maritime, avec poids ou volume selon le mode',
          'Colis manquants remontés au collecteur concerné',
          'Passage en transit et arrivée suivis automatiquement',
        ],
      },
      {
        id: 'delivery',
        title: 'Livraison et preuve',
        image: '/landing/delivery.jpg',
        alt: 'Remise en main propre d’un colis au destinataire',
        text: 'À destination, la remise est tracée : code de retrait communiqué au destinataire, '
          + 'photo et signature à la livraison, dépôt en point relais quand la remise directe '
          + "n'est pas possible.",
        points: [
          'Code de retrait vérifié au moment de la remise',
          'Incident signalé avec photo et notification immédiate',
          'Historique complet conservé sur le dossier du colis',
        ],
      },
    ],
  },
  en: {
    eyebrow: 'The platform',
    title: 'FretManager, from pickup to delivery',
    lede: 'Three moments structure a shipment. The platform handles them end to end, with no '
      + 're-entry and no loss of traceability between teams.',
    more: 'Learn more',
    blocks: [
      {
        id: 'registration',
        title: 'Package registration',
        image: '/landing/registration.jpg',
        alt: 'Labelled parcels next to a shipping label printer',
        text: 'A package is registered in four steps: route, sender and recipient, contents, '
          + 'payment. The price is suggested from the route pricing grid, the invoice follows '
          + 'immediately and the client receives confirmation.',
        points: [
          'Several packages for one client in a single entry',
          'Client declaration by QR code, validated by the partner',
          'Unique tracking code and printable label per package',
        ],
      },
      {
        id: 'departures',
        title: 'Departures and loading',
        image: '/landing/departures.jpg',
        alt: 'Cargo aircraft loaded with freight pallets on the tarmac',
        text: 'Each departure gathers its packages with a drop-off deadline and its flight or '
          + 'crossing. Loading is done by scanning: expected packages that are missing surface '
          + 'before departure, not after.',
        points: [
          'Air and sea, by weight or volume depending on the mode',
          'Missing packages reported to the relevant collector',
          'Transit and arrival tracked automatically',
        ],
      },
      {
        id: 'delivery',
        title: 'Delivery and proof',
        image: '/landing/delivery.jpg',
        alt: 'Parcel handed over to the recipient',
        text: 'On arrival, hand-over is traced: pickup code sent to the recipient, photo and '
          + 'signature on delivery, relay point drop-off when direct hand-over is not possible.',
        points: [
          'Pickup code verified at hand-over',
          'Incidents reported with photo and immediate notification',
          'Complete history kept on the package record',
        ],
      },
    ],
  },
};

const Solutions = () => {
  const { language } = useLanguage();
  const navigate = useNavigate();
  const copy = COPY[language === 'en' ? 'en' : 'fr'];

  return (
    <section id="solutions" className="landing-section">
      <div className="landing-section-inner">
        <span className="landing-section-eyebrow">{copy.eyebrow}</span>
        <h2 className="landing-section-title">{copy.title}</h2>
        <p className="landing-lede">{copy.lede}</p>

        <div className="landing-solutions">
          {copy.blocks.map((block, i) => (
            <article
              key={block.id}
              className={`landing-solution${i % 2 === 1 ? ' landing-solution--reverse' : ''}`}
            >
              <div className="landing-solution-media">
                <img src={block.image} alt={block.alt} loading="lazy" />
              </div>
              <div className="landing-solution-body">
                <h3>{block.title}</h3>
                <p>{block.text}</p>
                <ul>
                  {block.points.map((point) => <li key={point}>{point}</li>)}
                </ul>
                <button className="btn-link" onClick={() => navigate('/contact')}>
                  {copy.more}
                  <ArrowRight size={15} />
                </button>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Solutions;
